import { useCallback, useEffect, useState } from "react";

const nativeEl = () => document.fullscreenElement || document.webkitFullscreenElement || null;
const canNative = (el) => !!(el && (el.requestFullscreen || el.webkitRequestFullscreen));

// Fullscreen for a container ref. Uses the Fullscreen API where available and falls back to a fixed
// full-viewport overlay on iPhone Safari (which doesn't support element.requestFullscreen).
export function useFullscreen(ref) {
  const [isNative, setIsNative] = useState(false);
  const [overlay, setOverlay] = useState(false);

  useEffect(() => {
    const onChange = () => setIsNative(!!nativeEl() && nativeEl() === ref.current);
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, [ref]);

  // Esc exits the iOS overlay fallback (native fullscreen handles Esc itself).
  useEffect(() => {
    if (!overlay) return;
    const onKey = (e) => e.key === "Escape" && setOverlay(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [overlay]);

  const enter = useCallback(async () => {
    const el = ref.current;
    if (!el) return;
    if (canNative(el)) {
      try {
        await (el.requestFullscreen ? el.requestFullscreen() : el.webkitRequestFullscreen());
        return;
      } catch {
        /* iframe without allow=fullscreen, or blocked — fall back to overlay */
      }
    }
    setOverlay(true);
  }, [ref]);

  const exit = useCallback(async () => {
    if (nativeEl()) {
      try {
        await (document.exitFullscreen ? document.exitFullscreen() : document.webkitExitFullscreen());
      } catch {
        /* ignore */
      }
    }
    setOverlay(false);
  }, []);

  const active = isNative || overlay;
  const toggle = useCallback(() => (active ? exit() : enter()), [active, enter, exit]);
  return { active, overlay, toggle, enter, exit };
}
